import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useRouter, Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LogOut, Luggage, Search } from 'lucide-react';
import {
  getGuestProfile,
  updateGuestProfile,
  logoutGuest,
  listMyBookings,
  type GuestProfile,
  type MyBooking,
} from '@/api/generated';
import { Avatar } from '@/components/ui/avatar';
import { Eyebrow } from '@/components/ui/eyebrow';
import { Tabs } from '@/components/ui/tabs';
import { StatusChip } from '@/components/StatusChip';
import { Input } from '@/components/ui/input';
import { Button, buttonVariants } from '@/components/ui/button';
import { formatMoney } from '@/lib/booking';
import { identifyGuest } from '@/lib/posthog';
import { captureMagicLinkConsumed } from '@/lib/analytics';
import { applySeo } from '@/lib/seo';
import { resolveExistingBookingCancellationLabel } from '@/lib/calendar';
import { m } from '@/paraglide/messages';

// Cabinet page (/cabinet).
//
// Named cabinet.index.tsx, not cabinet.tsx: with a child route at
// /cabinet/bookings/$id (OH-94), a file named cabinet.tsx becomes that child's
// LAYOUT and renders in its place unless it renders an <Outlet/>. The symptom
// is the URL changing while the page does not — which is exactly how this was
// found.
// Post-login landing — the magic-link redirect targets this URL.
// Fetches the authenticated guest profile via GET /me. A 401 response
// means the session cookie is absent or expired; we redirect to /login.
export const Route = createFileRoute('/cabinet/')({
  component: CabinetPage,
});

type ProfileResult = GuestProfile | null; // null = 401, undefined = loading

function CabinetPage() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const identifiedRef = useRef(false);

  // noindex: authenticated guest portal — must not be crawled or indexed.
  useEffect(() => {
    return applySeo({ title: 'My Bookings — ollio', noindex: true });
  }, []);

  const profileQuery = useQuery<ProfileResult>({
    queryKey: ['guest-profile'],
    queryFn: async () => {
      const { data, response } = await getGuestProfile();
      if (response?.status === 401) {
        // Return sentinel — do not throw (avoids retry loop on 401).
        return null;
      }
      if (!data) {
        throw new Error('profile_fetch_failed');
      }
      return data;
    },
    retry: false,
    staleTime: 60_000,
  });

  const bookingsQuery = useQuery<MyBooking[]>({
    queryKey: ['my-bookings'],
    queryFn: async () => {
      const { data, error, response } = await listMyBookings();
      if (response?.status === 401) {
        // Profile query will handle the redirect; return empty for now.
        return [];
      }
      if (error || !data) {
        throw new Error('bookings_fetch_failed');
      }
      return data.bookings;
    },
    retry: false,
    staleTime: 60_000,
  });

  // 401 sentinel → redirect to /login.
  useEffect(() => {
    if (profileQuery.data === null) {
      void router.navigate({ to: '/login' });
    }
  }, [profileQuery.data, router]);

  // Identify the guest in PostHog once after first successful profile load
  // (ADR-028). Also fire magic_link.consumed once — the cabinet is the
  // magic-link redirect target, so first load = link consumed.
  useEffect(() => {
    if (profileQuery.data && !identifiedRef.current) {
      identifiedRef.current = true;
      identifyGuest(profileQuery.data.id);
      captureMagicLinkConsumed();
    }
  }, [profileQuery.data]);

  async function handleLogout() {
    try {
      await logoutGuest();
    } finally {
      // Clear all cached guest data regardless of the response status.
      queryClient.removeQueries({ queryKey: ['guest-profile'] });
      queryClient.removeQueries({ queryKey: ['my-bookings'] });
      void router.navigate({ to: '/login' });
    }
  }

  // `undefined` while loading or during the 401-redirect (sentinel null) — the
  // header degrades gracefully and the profile form is withheld until loaded.
  const profile = profileQuery.data ?? undefined;

  return (
    <main className="flex-1">
      <AccountHeader
        profile={profile}
        stays={bookingsQuery.data?.length}
        onLogout={() => void handleLogout()}
      />

      <div className="mx-auto w-full max-w-4xl space-y-12 px-6 py-10">
        {profileQuery.isError ? (
          <p className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-6 text-center text-muted shadow-[var(--shadow-sm)]">
            We could not load your account. Please refresh.
          </p>
        ) : (
          <>
            <BookingsTabs
              bookings={bookingsQuery.data ?? []}
              isLoading={bookingsQuery.isLoading}
              isError={bookingsQuery.isError}
            />
            {profile ? <ProfileForm profile={profile} /> : null}
          </>
        )}
      </div>
    </main>
  );
}

// ─── Account header (warm band) ───────────────────────────────────────────────

export function AccountHeader({
  profile,
  stays,
  onLogout,
}: {
  profile?: GuestProfile;
  stays?: number;
  onLogout: () => void;
}) {
  const firstName = (profile?.full_name ?? '').trim().split(/\s+/)[0] || 'guest';
  return (
    <section className="border-b border-[var(--border-hairline)] bg-[var(--surface-warm)]">
      <div className="mx-auto flex w-full max-w-4xl flex-wrap items-center gap-5 px-6 py-10">
        <Avatar name={profile?.full_name || profile?.email} size="xl" ring />
        <div className="min-w-[160px] flex-1">
          <Eyebrow className="mb-1.5">My Cabinet</Eyebrow>
          <h1 className="font-serif text-3xl font-semibold leading-tight tracking-tight text-[var(--ollio-ink)] sm:text-4xl">
            Welcome back, {firstName}
          </h1>
          {typeof stays === 'number' ? (
            <p className="mt-1.5 text-sm text-muted">
              {stays} {stays === 1 ? 'stay' : 'stays'} with Ollio
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Entry point to search — the home page carries the functional bar. */}
          <Link to="/" className={buttonVariants({ variant: 'secondary', pill: true })}>
            <Search className="h-[18px] w-[18px]" />
            Find a stay
          </Link>
          <Button
            variant="ghost"
            pill
            leadingIcon={<LogOut className="h-[18px] w-[18px]" />}
            onClick={onLogout}
          >
            Sign out
          </Button>
        </div>
      </div>
    </section>
  );
}

// ─── Bookings (Upcoming / Past tabs) ──────────────────────────────────────────
function BookingStatus({ status }: { status: string }) {
  return <StatusChip status={status} />;
}


export function BookingsTabs({
  bookings,
  isLoading,
  isError,
}: {
  bookings: MyBooking[];
  isLoading: boolean;
  isError: boolean;
}) {
  const [tab, setTab] = useState<'upcoming' | 'past'>('upcoming');
  // String compare is valid for ISO YYYY-MM-DD dates.
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = bookings.filter((b) => b.checkout_date >= today);
  const pastList = bookings.filter((b) => b.checkout_date < today);
  const list = tab === 'upcoming' ? upcoming : pastList;

  return (
    <section>
      <Tabs
        value={tab}
        onChange={(v) => setTab(v as 'upcoming' | 'past')}
        tabs={[
          { value: 'upcoming', label: 'Upcoming', count: upcoming.length },
          { value: 'past', label: 'Past Stays', count: pastList.length },
        ]}
        className="mb-6"
      />

      {isLoading ? (
        <div className="space-y-4">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="h-28 animate-pulse rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card"
            />
          ))}
        </div>
      ) : isError ? (
        <p className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-6 text-center text-muted shadow-[var(--shadow-sm)]">
          Could not load bookings. Please refresh.
        </p>
      ) : list.length === 0 ? (
        <EmptyBookings tab={tab} />
      ) : (
        <div className="space-y-4">
          {list.map((b) => (
            <BookingCard key={b.id} booking={b} />
          ))}
        </div>
      )}
    </section>
  );
}

function BookingCard({ booking }: { booking: MyBooking }) {
  const total = formatMoney(booking.total_amount_minor, booking.currency);
  // OH-68 §3.5: THIS booking's own cancellation-policy snapshot, not
  // today's canonical policy. Gated (via resolveExistingBookingCancellationLabel)
  // on status==='confirmed' AND the deadline itself still being in the
  // future — review round 1, IMPORTANT 1: this cabinet's own Past Stays tab
  // splits on checkout_date, not status, and nothing in this codebase ever
  // moves a booking OUT of 'confirmed' (no write path sets checked_in/
  // checked_out), so a stay from years ago is still 'confirmed' today —
  // status alone rendered a deadline that had already lapsed. Also renders
  // nothing for an empty/unreadable snapshot (every pre-OH-68 booking) or
  // one with no 100%-refund tier, same pinned decision as the booking-view
  // page.
  const cancellationDeadlineLabel = resolveExistingBookingCancellationLabel(
    booking.status,
    booking.free_cancellation,
  );
  return (
    // OH-94: the card is now a link. Everything it shows is a summary of a
    // page that answers the rest — what was paid for, for whom, and how to
    // get out of it — and until that page existed the card was a dead end.
    <Link
      to="/cabinet/bookings/$id"
      params={{ id: booking.id }}
      className="block rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5 shadow-[var(--shadow-sm)] transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-lg)] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-glow)]"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <BookingStatus status={booking.status} />
          <h3 className="mt-2 font-serif text-xl font-semibold leading-snug tracking-tight text-[var(--ollio-ink)]">
            {booking.apartment_title}
          </h3>
          <p className="mt-1 text-sm text-muted">
            {booking.checkin_date} – {booking.checkout_date}
            {' · '}
            {booking.nights} {booking.nights === 1 ? 'night' : 'nights'}
          </p>
          {booking.ota_reservation_code ? (
            <p className="mt-2 text-xs text-muted">
              Reservation: <span className="font-mono">{booking.ota_reservation_code}</span>
            </p>
          ) : null}
          {cancellationDeadlineLabel ? (
            <p className="mt-2 text-xs text-muted">
              {m.cancellation_deadline({ date: cancellationDeadlineLabel })}
            </p>
          ) : null}
        </div>
        <p className="shrink-0 font-mono text-lg font-semibold tabular-nums text-[var(--ollio-ink)]">
          {total}
        </p>
      </div>
    </Link>
  );
}

function EmptyBookings({ tab }: { tab: 'upcoming' | 'past' }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card px-6 py-14 text-center shadow-[var(--shadow-sm)]">
      <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--surface-warm)]">
        <Luggage className="h-7 w-7 text-[var(--ollio-gold-deep)]" aria-hidden="true" />
      </div>
      <h3 className="mb-2 font-serif text-2xl font-semibold text-[var(--ollio-ink)]">
        {tab === 'upcoming' ? 'No upcoming stays yet' : 'No past stays'}
      </h3>
      <p className="mx-auto mb-6 max-w-sm text-[15px] leading-relaxed text-muted">
        {tab === 'upcoming'
          ? "When you book a Belgrade home, it'll live here — with your itinerary all in one place."
          : 'Your completed stays will appear here.'}
      </p>
      {tab === 'upcoming' ? (
        <Link to="/" className={buttonVariants({ variant: 'gold', pill: true })}>
          Explore apartments
        </Link>
      ) : null}
    </div>
  );
}

// ─── Profile form ─────────────────────────────────────────────────────────────

export function ProfileForm({ profile }: { profile: GuestProfile }) {
  const queryClient = useQueryClient();
  const [fullName, setFullName] = useState(profile.full_name ?? '');
  const [phone, setPhone] = useState(profile.phone ?? '');
  const [language, setLanguage] = useState(profile.language ?? '');
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  async function handleSave(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSaveState('saving');
    const body: Parameters<typeof updateGuestProfile>[0]['body'] = {};
    if (fullName.trim()) body.full_name = fullName.trim();
    if (phone.trim()) body.phone = phone.trim();
    if (language.trim()) body.language = language.trim();
    try {
      const { data, response } = await updateGuestProfile({ body });
      if (response?.status === 200 && data) {
        queryClient.setQueryData<ProfileResult>(['guest-profile'], data);
        setSaveState('saved');
        setTimeout(() => setSaveState('idle'), 2000);
      } else {
        setSaveState('error');
      }
    } catch {
      setSaveState('error');
    }
  }

  return (
    <section>
      <h2 className="mb-4 font-serif text-2xl font-semibold tracking-tight text-[var(--ollio-ink)]">
        Profile
      </h2>
      <div className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-6 shadow-[var(--shadow-sm)]">
        <form onSubmit={(e) => void handleSave(e)} className="space-y-4">
          {/* Email is read-only — it comes from the magic link. */}
          <Input label="Email" type="email" disabled value={profile.email ?? ''} readOnly />
          <Input
            label="Full name"
            type="text"
            autoComplete="name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Your name"
          />
          <Input
            label="Phone"
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 555 000 0000"
          />
          <Input
            label="Preferred language"
            type="text"
            autoComplete="language"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            placeholder="en"
            hint="BCP-47 code, e.g. en, ru, de"
          />

          {saveState === 'error' ? (
            <p role="alert" className="text-sm text-destructive">
              Could not save your profile. Please try again.
            </p>
          ) : null}

          <div className="flex items-center gap-3">
            <Button
              type="submit"
              variant="primary"
              disabled={saveState === 'saving'}
              loading={saveState === 'saving'}
            >
              {saveState === 'saving' ? 'Saving…' : 'Save'}
            </Button>
            {saveState === 'saved' ? (
              <p role="status" className="text-sm text-[var(--ollio-success)]">
                Saved.
              </p>
            ) : null}
          </div>
        </form>
      </div>
    </section>
  );
}
